import React, { useState, useRef, useEffect, useContext, createContext } from "react";
import { Link, NavLink as RouterNavLink, useLocation } from "react-router-dom";
import logo from "../../assets/icons/Logo.jpeg"; // Your logo
import { useAuth } from "../../contexts/AuthContext"; // Import the useAuth hook

// --- 1. Create Theme Context ---
const ThemeContext = createContext();

// --- 2. Custom Theme Hook (to manage theme state and side effects) ---
export const useTheme = () => {
  return useContext(ThemeContext);
};

// --- 3. Theme Provider Component ---
export const ThemeProvider = ({ children }) => {
  // Initialize state from local storage or default to 'light'
  const [theme, setTheme] = useState(
    localStorage.getItem('theme') || 'light'
  );

  // Effect to apply the class to the body and update local storage
  useEffect(() => {
    document.body.className = theme === 'dark' ? 'dark-mode' : '';
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Toggle function
  const toggleTheme = () => {
    setTheme(currentTheme => (currentTheme === 'light' ? 'dark' : 'light'));
  };

  const value = { theme, toggleTheme };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// --- 4. Navbar Component (Integrated with Theme Hook) ---
export default function Navbar() {
  // --- Auth State ---
  const { user, logout } = useAuth();

  // --- Theme Hook Integration ---
  // NOTE: You must wrap your App component with <ThemeProvider> for this to work.
  const { theme, toggleTheme } = useTheme(); 

  // --- UI State ---
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  // --- Logic to hide navbar on auth pages ---
  const location = useLocation();
  const isAuthPage =
    location.pathname === "/login" || location.pathname === "/signup";

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
        setConvertOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Helper to close all menus on navigation
  const closeAllMenus = () => {
    setDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  // Helper for logout (closes mobile menu)
  const handleLogout = () => {
    logout();
    closeAllMenus();
  };

  // Helper for main nav links
  const NavLink = ({ to, children }) => (
    <RouterNavLink to={to} onClick={closeAllMenus}>
      {children}
    </RouterNavLink>
  );

  // --- Theme Toggle Button Component ---
  const ThemeToggle = ({ isMobile = false }) => (
    <button
      onClick={toggleTheme}
      className={`${isMobile ? 'mobile-theme-toggle' : 'desktop-theme-toggle'} theme-toggle-btn`}
      aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
    >
      <i 
        className={theme === 'light' ? "fas fa-moon" : "fas fa-sun"}
        // Add text for mobile, just icon for desktop
      ></i> {isMobile && (theme === 'light' ? 'Dark Mode' : 'Light Mode')}
    </button>
  );

  return (
    <header className={`navbar ${isAuthPage ? "navbar-small" : ""}`}>
      <div className="logo">
        <Link to="/" onClick={closeAllMenus}>
          <img src={logo} alt="PDF Play Logo" className="logo-img" />
        </Link>
      </div>

      {/* --- Desktop Navigation (Hidden on auth pages) --- */}
      {!isAuthPage && (
        <nav className="nav-links">
          {/* Main Links */}
          <NavLink to="/split-pdf">SPLIT PDF</NavLink>
          <NavLink to="/number-pages">ADD PAGE NUMBERS</NavLink>
          <NavLink to="/merge-pdf">MERGE PDF</NavLink>
          
          {/* Theme Toggle for Desktop */}
          <ThemeToggle />

          {/* Desktop Dropdown */}
          <div className="dropdown" ref={dropdownRef}>
            <button
              className="dropdown-toggle"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              ALL PDF TOOLS <i className="fas fa-caret-down"></i>
            </button>
            {dropdownOpen && (
              <ul className="dropdown-menu">
                {/* ... existing dropdown content ... */}
                <li><NavLink to="/merge-pdf">Merge PDF</NavLink></li>
                <li><NavLink to="/split-pdf">Split PDF</NavLink></li>
                <li><NavLink to="/organize-pdf">Organize PDF</NavLink></li>
                <li><NavLink to="/rotate-pdf">Rotate PDF</NavLink></li>
                <li><NavLink to="/delete-pages">Delete PDF Pages</NavLink></li>
                <li className="separator"></li>
                <li><NavLink to="/edit-pdf">Edit PDF</NavLink></li>
                <li><NavLink to="/sign-pdf">Sign PDF</NavLink></li>
                <li><NavLink to="/add-watermark">Add Watermark</NavLink></li>
                <li><NavLink to="/number-pages">Add Page Numbers</NavLink></li>
                <li><NavLink to="/crop-pdf">Crop PDF</NavLink></li>
                <li><NavLink to="/redact-pdf">Redact PDF</NavLink></li>
                <li className="separator"></li>
                <li><NavLink to="/protect-pdf">Protect PDF</NavLink></li>
                <li className="separator"></li>
                {/* --- CONVERT (Nested) --- */}
                <li
                  className="nested-dropdown"
                  onMouseEnter={() => setConvertOpen(true)}
                  onMouseLeave={() => setConvertOpen(false)}
                >
                  <span>
                    Convert PDF <i className="fas fa-caret-right"></i>
                  </span>
                  {convertOpen && (
                    <ul className="nested-menu">
                      <li><strong>Convert to PDF</strong></li>
                      <li><NavLink to="/jpg-to-pdf">JPG to PDF</NavLink></li>
                      <li><NavLink to="/word-to-pdf">Word to PDF</NavLink></li>
                      <li><NavLink to="/pptx-to-pdf">PowerPoint to PDF</NavLink></li>
                      <li><NavLink to="/excel-to-pdf">Excel to PDF</NavLink></li>
                      <li><NavLink to="/html-to-pdf">HTML to PDF</NavLink></li>
                      <li className="separator"></li>
                      <li><strong>Convert from PDF</strong></li>
                      <li><NavLink to="/pdf-to-jpg">PDF to JPG</NavLink></li>
                      <li><NavLink to="/pdf-to-word">PDF to Word</NavLink></li>
                      <li><NavLink to="/pdf-to-pptx">PDF to PowerPoint</NavLink></li>
                      <li><NavLink to="/pdf-to-excel">PDF to Excel</NavLink></li>
                    </ul>
                  )}
                </li>
              </ul>
            )}
          </div>
        </nav>
      )}

      {/* --- Auth Buttons (Conditional Logic) --- */}
      <div className="auth-buttons">
        {!isAuthPage && // Only show auth buttons if NOT on login/signup
          (user ? (
            <>
              {/* Show email or username here */}
              <span className="navbar-username">{user.username}</span>
              <button onClick={handleLogout} className="login-btn">
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="login-btn">
                Login
              </Link>
              <Link to="/signup" className="signup-btn">
                Sign up
              </Link>
            </>
          ))}
      </div>

      {/* --- Mobile Hamburger Button (Hidden on auth pages) --- */}
      {!isAuthPage && (
        <button
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <i className={isMobileMenuOpen ? "fas fa-times" : "fas fa-bars"}></i>
        </button>
      )}

      {/* --- Mobile Menu (Fullscreen Overlay) --- */}
      {isMobileMenuOpen && (
        <nav className="mobile-nav">
          <ThemeToggle isMobile={true} /> {/* Theme Toggle for Mobile */}
          <li className="mobile-nav-divider"></li>

          <NavLink to="/merge-pdf">Merge PDF</NavLink>
          <NavLink to="/split-pdf">Split PDF</NavLink>
          <NavLink to="/pdf-to-word">PDF to Word</NavLink>
          <NavLink to="/word-to-pdf">Word to PDF</NavLink>
          <NavLink to="/pdf-to-jpg">PDF to JPG</NavLink>
          <NavLink to="/jpg-to-pdf">JPG to PDF</NavLink>
          {/* ... Add all your other tool links here ... */}

          <li className="mobile-nav-divider"></li>

          {user ? (
            <button onClick={handleLogout} className="mobile-signup-btn">
              Logout
            </button>
          ) : (
            <>
              <NavLink to="/login" className="mobile-login-btn">
                Login
              </NavLink>
              <NavLink to="/signup" className="mobile-signup-btn">
                Sign up
              </NavLink>
            </>
          )}
        </nav>
      )}
    </header>
  );
}